import { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Badge, Form, ButtonGroup, Modal } from 'react-bootstrap';
import PageHeader from '../../components/PageHeader';
import Swal from 'sweetalert2';
import { apiUrl } from '../../config/apiUrls';
import { useNavigate } from 'react-router-dom';
import { usePaymentStore } from '../../hooks/ai-hooks/usePaymentCredit';
import useRefreshStore from '../../hooks/ai-hooks/useRefreshStore';
import { generateSlug } from '../../config/generateDebateSlug';
import { useUserTopic } from '../../hooks/ai-hooks/useCommSkillsTopic';
import { v4 as uuidv4 } from 'uuid';

function CommunicationSkills() {
  const router = useNavigate()
  const BASE_URL = process.env.REACT_APP_AI_API_URL
  const { payType, credits } = usePaymentStore()
  const { setHasRefreshed } = useRefreshStore()
  const [isStarting, setIsStarting] = useState(false);
  const [loader, setLoader] = useState(false)
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const studentId = sessionStorage.getItem("id")
  const [formData, setFormData] = useState({
    practiceType: "everyday_communication",
    focusArea: "fluency",
    difficulty: "beginner",
    duration: "10"
  });

  useEffect(() => {
    setHasRefreshed(false)
  }, [])

  const handleStart = () => {
    setIsStarting(true);
    setTimeout(() => {
      setIsStarting(false);
      console.log('Starting AI Communication Skills Session...');
    }, 2000);
  };

  const setupChecklist = [
    {
      id: 1,
      title: "Microphone Access",
      desc: "Enable microphone permissions for voice interaction",
      icon: "bi-mic-fill",
      required: true
    },
    {
      id: 2,
      title: "Camera ( Optional )",
      desc: "Enable camera for non-verbal cues analysis",
      icon: "bi-camera-video-fill",
      required: false
    },
    {
      id: 3,
      title: "Stable Internet",
      desc: "Ensure 5+ Mbps connection for smooth experience",
      icon: "bi-wifi",
      required: true
    },
    {
      id: 4,
      title: "Quiet Environment",
      desc: "Choose a quiet space to minimize background noise",
      icon: "bi-volume-mute-fill",
      required: true
    },
    {
      id: 5,
      title: "Headphones ( Recommended )",
      desc: "Use headphones for better audio clarity",
      icon: "bi-headphones",
      required: false
    }
  ];

  const howItWorksSteps = [
    {
      step: "1",
      title: "AI Initiates Conversation",
      desc: "The AI will introduce a communication scenario based on professional contexts"
    },
    {
      step: "2",
      title: "Voice Interaction",
      desc: "Respond naturally using your voice. Speak as you would in a real conversation"
    },
    {
      step: "3",
      title: "Dynamic Role-Play",
      desc: "Engage in realistic scenarios like client calls, team meetings, or presentations"
    },
    {
      step: "4",
      title: "Instant Analysis",
      desc: "Receive real-time feedback on your communication effectiveness and areas for improvement"
    }
  ];

  const startCommunication = async (payload) => {
    try {
      const response = await fetch(
        `${BASE_URL}${apiUrl.CommunicationPrepStart}`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      // ✅ SUCCESS
      if (response.status === 200) {
        if (result?.paymentData) {
          usePaymentStore.getState().setPaymentData(result.paymentData);
        }

        router(`/practice-with-ai/communication-skills/${result.uniqueId}`);
        return;
      }

      if (response.status === 402) {
        if (result?.paymentData) {
          usePaymentStore.getState().setPaymentData(result.paymentData);
        }
        Swal.fire('Insufficient Credits', "You’ve used all credits. Upgrade your plan to continue practicing and improving 🚀", 'warning')

        return;
      }

      Swal.fire(
        "Failed to Start",
        result?.message || "Please try again later",
        "error"
      );
    } catch (error) {
      console.error(error);
      Swal.fire("Failed to Start", "Network error. Please try again.", "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoader(true)
    const uniqueId = uuidv4();

    const payload = {
      duration: formData.duration,
      level: formData.difficulty,
      prepType: formData.practiceType,
      focusArea: formData.focusArea,
      userId: studentId,
      uniqueId,
      topic: generateSlug(formData.practiceType, formData.difficulty, formData.focusArea, formData.duration)
    };

    try {
      await startCommunication({ ...payload, uniqueId });
      useUserTopic.getState().setModuleData("communication", payload);
    } catch (error) {
      console.error(error)

    } finally {
      setLoader(false)
    }
  }

  const handleChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return (
    <>
      <Row>
        <Col>
          <PageHeader
            title="AI Communication Skills Preparation"
            breadcrumbs={[
              { label: "Dashboard", path: "/dashboard" },
              { label: "Practice With AI", path: '/practice-with-ai' },
              { label: "Communication Skills", active: true },
            ]}
          />
        </Col>
      </Row>

      <Container className="py-4">
        <Row className="g-4">
          <Col lg={6} className="order-2 order-lg-1">
            <Card className="border rounded-md mb-4">
              <Card.Header className="bg-white border-0 pt-4 px-4 ">
                <h5 className="fw-bold mb-1">
                  <i className="bi bi-gear-fill text-primary me-2"></i>
                  Setup Requirements
                </h5>
                <small className="text-muted">Ensure you meet these requirements before starting</small>
              </Card.Header>
              <Card.Body className="px-4 pb-4">
                {setupChecklist.map((item, idx) => (
                  <div
                    key={item.id}
                    className={`d-flex align-items-start p-3 rounded-3 ${idx !== setupChecklist.length - 1 ? 'mb-2' : ''}`}
                    style={{ backgroundColor: '#f8f9fa' }}
                  >
                    <div className="me-3">
                      <i className={`bi ${item.icon} fs-4 text-primary`}></i>
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center mb-1">
                        <h6 className="mb-0 fw-semibold">{item.title}</h6>
                        {item.required && (
                          <Badge bg="danger" className="ms-2 px-2 py-1" style={{ fontSize: '0.65rem' }}>
                            Required
                          </Badge>
                        )}
                      </div>
                      <small className="text-muted">{item.desc}</small>
                    </div>
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6} className="order-1 order-lg-2">
            <div className="d-flex align-items-center justify-content-center w-full">
              <Card className="w-100" >
                <Card.Body>
                  <div className='text-center p-3 border-bottom'>
                    <h5 className="fw-bold mb-1">
                      <i className="ri-chat-voice-fill text-primary fs-4 me-2"></i>
                      Communication Skills
                    </h5>
                    <p className='text-muted'>Master the art of effective communication</p>
                  </div>
                  <Form onSubmit={handleSubmit} className="d-flex flex-column gap-3" style={{ marginTop: "17px" }}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Practice Type</Form.Label>
                      <Form.Select
                        value={formData.practiceType}
                        aria-label="Practice Type"
                        onChange={(e) => handleChange("practiceType", e.target.value)}
                      >
                        <option value="everyday_communication">Everyday Communication</option>
                        <option value="professional_communication">Professional Communication</option>
                      </Form.Select>
                    </Form.Group>

                    {/* Focus Area */}
                    <Form.Group>
                      <Form.Label className="fw-semibold">Focus Area</Form.Label>
                      <Form.Select
                        value={formData.focusArea}
                        onChange={(e) => handleChange("focusArea", e.target.value)}
                      >
                        <option value="fluency">Fluency</option>
                        <option value="vocabulary">Vocabulary</option>
                        <option value="grammar">Grammar</option>
                      </Form.Select>
                    </Form.Group>

                    {/* Difficulty */}
                    <Form.Group>
                      <Form.Label className="fw-semibold">Difficulty Level</Form.Label>
                      <div className="d-flex justify-content-between mt-2">
                        {["beginner", "intermediate", "advanced"].map((level) => (
                          <Form.Check
                            key={level}
                            type="radio"
                            label={level.charAt(0).toUpperCase() + level.slice(1)}
                            name="difficulty"
                            value={level}
                            checked={formData.difficulty === level}
                            onChange={() => handleChange("difficulty", level)}
                          />
                        ))}
                      </div>
                    </Form.Group>

                    {/* Time Selector */}
                    <div className="d-flex align-items-center justify-content-between">
                      <Form.Label className="fw-semibold mb-0">Select Minutes</Form.Label>

                      <ButtonGroup>
                        {["5", "10", "15"].map((time) => (
                          <Button
                            key={time}
                            variant={formData.duration === time ? "primary" : "outline-primary"}
                            onClick={() => handleChange("duration", time)}
                          >
                            {time} min
                          </Button>
                        ))}
                      </ButtonGroup>
                    </div>

                    <Button
                      variant="primary"
                      size="lg"
                      className="w-100 py-2 fw-bold shadow-sm"
                      type='submit'
                    >
                      {loader ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Initializing Session...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-play-circle-fill me-2"></i>
                          Start Communication Practice
                        </>
                      )}
                    </Button>
                  </Form>
                  <div className='text-end mt-3'>
                    <Button
                      variant="link"
                      className="p-0 text-decoration-none small fw-semibold"
                      onClick={() => setShowHowItWorks(true)}
                    >
                      How it works?
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>

      {/* How It Works Modal */}
      <Modal
        show={showHowItWorks}
        onHide={() => setShowHowItWorks(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="border-0 pb-0 px-4 pt-4">
          <Modal.Title className="fw-bold">
            <i className="bi bi-info-circle-fill text-primary me-2"></i>
            How It Works
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 pb-4 pt-3">
          <Row className="g-4">
            {howItWorksSteps.map((item, idx) => (
              <Col md={6} key={idx}>
                <div
                  className="h-100 p-3 rounded-3 border bg-light d-flex flex-column gap-2"
                >
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold"
                      style={{ width: '28px', height: '28px', fontSize: '0.8rem' }}
                    >
                      {item.step}
                    </div>
                    <h6 className="fw-bold mb-0">{item.title}</h6>
                  </div>
                  <p className="text-muted small mb-0">{item.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
          <div className="mt-4 text-center">
            <Button variant="primary" onClick={() => setShowHowItWorks(false)} className="px-4 fw-semibold">
              Got it, let's start!
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default CommunicationSkills;